import { useMemo, useState } from 'react';

import { Dialog, DialogContent, Grid } from '@mui/material';
import { User } from '@prisma/client';

import { useMutation } from '@redwoodjs/web';

import ModalContent from 'src/components/ModalContent/ModalContent';
import { ConfirmationModal } from 'src/components/Modals';
import { NPIDataGrid, NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DELETE_NOTE } from 'src/graphql/mutations/notes.mutation';
import { formatDate } from 'src/utils/utils';

type UserSubset = Pick<User, 'id' | 'firstName' | 'lastName'>;
type Note = {
    id: number;
    subject: string;
    body: string;
    createdAt: string;
    createdBy: UserSubset;
};
interface NotesProps {
    notes: Note[];
    refetch: () => void;
    isMerchant?: boolean;
    maxHeight?: string;
    hideDelete?: boolean;
}

const Notes = ({ notes, refetch, isMerchant, maxHeight, hideDelete }: NotesProps) => {
    const [openViewModal, setOpenViewModal] = useState(false);
    const [viewNote, setViewNote] = useState<string>();

    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
    const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    const [deleteNote] = useMutation(DELETE_NOTE);

    const tableData = useMemo(() => {
        if (notes?.length) {
            return notes.map((note) => {
                const dateCreated = note?.createdAt;
                const createdBy = isMerchant
                    ? note?.createdBy
                    : `${note?.createdBy?.firstName} ${note?.createdBy?.lastName}`;

                return {
                    ...note,
                    dateCreated,
                    createdBy,
                };
            });
        }

        return [];
    }, [isMerchant, notes]);

    const handleOpenViewModal = (note: Note) => {
        setViewNote(note?.body);
        setOpenViewModal(true);
    };

    const handleCloseViewModal = () => {
        setOpenViewModal(false);
    };

    const exitDeleteMode = () => {
        setIsDeleteMode(false);
        setSelectedIds([]);
    };

    const enterDeleteMode = () => {
        setIsDeleteMode(true);
    };

    const openBulkConfirm = () => {
        if (selectedIds.length === 0) {
            return;
        }
        setIsBulkConfirmOpen(true);
    };

    const confirmBulkDelete = async () => {
        if (isBulkDeleting) {
            return;
        }
        setIsBulkDeleting(true);

        try {
            for (const id of selectedIds) {
                await deleteNote({
                    variables: { id: Number(id) },
                });
            }
            showSuccessToast(`Deleted ${selectedIds.length} note(s)`);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_error) {
            showErrorToast('Error deleting selected notes');
        } finally {
            setIsBulkDeleting(false);
            setIsBulkConfirmOpen(false);
            exitDeleteMode();
            refetch();
        }
    };

    const NOTES_TABLE_HEADERS: NPIDataGridColDef[] = [
        createColumnDef({
            field: 'dateCreated',
            headerName: 'Date/Time',
            renderCell: (params) => {
                const date = new Date(params?.row?.createdAt);
                return formatDate({ date });
            },
        }),
        createColumnDef({
            field: 'subject',
            headerName: 'Subject',
        }),
        createColumnDef({
            field: 'createdBy',
            headerName: 'Added By',
        }),
    ];

    return (
        <Grid sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <NPIDataGrid
                dataTestId="notes-table"
                rows={tableData}
                columns={NOTES_TABLE_HEADERS}
                getRowId={(row) => {
                    return row.id;
                }}
                handleView={(row) => {
                    handleOpenViewModal(row);
                }}
                title="Notes"
                maxHeight={maxHeight}
                onSelectionChange={(ids) => {
                    setSelectedIds(ids);
                }}
                selectedRowIds={selectedIds}
                deleteModeConfig={{
                    isActive: isDeleteMode,
                    hasPermission: !hideDelete,
                    onEnter: enterDeleteMode,
                    onCancel: exitDeleteMode,
                    onConfirm: openBulkConfirm,
                    disableConfirm: selectedIds.length === 0,
                }}
            />

            <ConfirmationModal
                isOpen={isBulkConfirmOpen}
                title="Delete Note?"
                cancelBtnVariant="back-button"
                dialogActionMarginTop={'14px'}
                message={
                    <>
                        Are you sure you want to delete {selectedIds.length} item
                        {selectedIds.length !== 1 && 's'}?
                    </>
                }
                onCancel={() => {
                    setIsBulkConfirmOpen(false);
                }}
                onConfirm={confirmBulkDelete}
                cancelText="No"
                confirmText="Yes"
                loading={isBulkDeleting}
            />

            <Dialog
                open={openViewModal}
                onClose={handleCloseViewModal}
                aria-labelledby="view-note-offer-dialog"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={handleCloseViewModal}
                        isViewModal
                        isActionDisabled
                        header="View Note"
                        message={viewNote}
                        closeModalButtonText="Close"
                        handleAction={() => {}}
                    />
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default Notes;
